import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface StandardTerm {
  id: string;
  name: string;
  description: string | null;
  content: string | null;
  document_url: string | null;
  is_default: boolean;
  sort_order: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export const useStandardTerms = () => {
  const queryClient = useQueryClient();

  const { data: terms = [], isLoading: loading } = useQuery({
    queryKey: ['standard-terms'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('quote_standard_terms')
        .select('*')
        .is('deleted_at', null)
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data as StandardTerm[];
    },
  });

  const createTerm = useMutation({
    mutationFn: async (term: Partial<StandardTerm>) => {
      const { data: user } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('quote_standard_terms')
        .insert({
          name: term.name!,
          description: term.description,
          content: term.content,
          document_url: term.document_url,
          is_default: term.is_default ?? false,
          sort_order: term.sort_order ?? 0,
          created_by: user.user?.id,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['standard-terms'] });
      toast.success('Standaard voorwaarden toegevoegd');
    },
    onError: () => {
      toast.error('Fout bij toevoegen voorwaarden');
    },
  });

  const updateTerm = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<StandardTerm> & { id: string }) => {
      const { data, error } = await supabase
        .from('quote_standard_terms')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['standard-terms'] });
      toast.success('Voorwaarden bijgewerkt');
    },
    onError: () => {
      toast.error('Fout bij bijwerken voorwaarden');
    },
  });

  const deleteTerm = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('quote_standard_terms')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['standard-terms'] });
      toast.success('Voorwaarden verwijderd');
    },
    onError: () => {
      toast.error('Fout bij verwijderen voorwaarden');
    },
  });

  return {
    terms,
    loading,
    createTerm: createTerm.mutateAsync,
    updateTerm: updateTerm.mutateAsync,
    deleteTerm: deleteTerm.mutateAsync,
    isCreating: createTerm.isPending,
    isUpdating: updateTerm.isPending,
  };
};

// Hook for getting selected terms for a template
export const useTemplateTerms = (templateId: string | undefined) => {
  const queryClient = useQueryClient();

  const { data: selectedTermIds = [], isLoading } = useQuery({
    queryKey: ['template-terms', templateId],
    queryFn: async () => {
      if (!templateId) return [];
      
      const { data, error } = await supabase
        .from('template_default_terms')
        .select('terms_id')
        .eq('template_id', templateId)
        .order('sort_order');
      
      if (error) throw error;
      return data.map(t => t.terms_id);
    },
    enabled: !!templateId,
  });

  const setSelectedTerms = useMutation({
    mutationFn: async (termIds: string[]) => {
      if (!templateId) throw new Error('No template ID');
      
      // Remove existing
      await supabase
        .from('template_default_terms')
        .delete()
        .eq('template_id', templateId);
      
      // Add new
      if (termIds.length > 0) {
        const { error } = await supabase
          .from('template_default_terms')
          .insert(
            termIds.map((terms_id, index) => ({
              template_id: templateId,
              terms_id,
              sort_order: index,
            }))
          );
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template-terms', templateId] });
    },
  });

  return {
    selectedTermIds,
    isLoading,
    setSelectedTerms: setSelectedTerms.mutateAsync,
  };
};

// Hook for getting selected terms for a quote
export const useQuoteTerms = (quoteId: string | undefined) => {
  const queryClient = useQueryClient();

  const { data: selectedTermIds = [], isLoading } = useQuery({
    queryKey: ['quote-terms', quoteId],
    queryFn: async () => {
      if (!quoteId) return [];
      
      const { data, error } = await supabase
        .from('quote_selected_terms')
        .select('terms_id')
        .eq('quote_id', quoteId)
        .order('sort_order');
      
      if (error) throw error;
      return data.map(t => t.terms_id);
    },
    enabled: !!quoteId,
  });

  const setSelectedTerms = useMutation({
    mutationFn: async (termIds: string[]) => {
      if (!quoteId) throw new Error('No quote ID');
      
      // Remove existing
      await supabase
        .from('quote_selected_terms')
        .delete()
        .eq('quote_id', quoteId);
      
      // Add new
      if (termIds.length > 0) {
        const { error } = await supabase
          .from('quote_selected_terms')
          .insert(
            termIds.map((terms_id, index) => ({
              quote_id: quoteId,
              terms_id,
              sort_order: index,
            }))
          );
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote-terms', quoteId] });
    },
  });

  return {
    selectedTermIds,
    isLoading,
    setSelectedTerms: setSelectedTerms.mutateAsync,
  };
};
